import axios from "axios";
import { toast } from "sonner";

const CONFIGURED_BACKEND = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";
// Accept either the documented origin form or an older value ending in /api;
// normalizing here prevents an easy-to-miss /api/api authentication URL.
const NORMALIZED_BACKEND = CONFIGURED_BACKEND.replace(/\/+$/, "");
export const BACKEND = NORMALIZED_BACKEND === "/api"
  ? ""
  : NORMALIZED_BACKEND.replace(/^(https?:\/\/[^/]+)\/api$/i, "$1");
export const API_BASE = `${BACKEND}/api`;
export const IS_PRODUCTION = process.env.REACT_APP_PRODUCTION === "true";
export const AUTH_SESSION_EXPIRED_EVENT = "shreehomes:auth-session-expired";
export const AUTH_CHANNEL_NAME = "shreehomes-auth";
export const AUTH_SYNC_STORAGE_KEY = "shreehomes:auth-event";
export const AUTH_LOGOUT_INTENT_STORAGE_KEY = "shreehomes:auth-logout-intent";
export const AUTH_LOCK_NAME = "shreehomes-auth-refresh";
export const COOKIE_AUTH_HEADER = "X-Auth-Mode";
export const COOKIE_AUTH_MODE = "cookie";
export const CSRF_HEADER = "X-XSRF-TOKEN";

export const api = axios.create({
  baseURL: API_BASE,
  // Browser sessions are carried only by Secure, httpOnly cookies. This must
  // be enabled for both same-origin production traffic and cross-port/local
  // development traffic so the browser accepts and sends those cookies.
  withCredentials: true,
  // CSRF is copied from the /auth/csrf response into module memory below. Do
  // not let Axios re-read a same-named cookie from the frontend host: in a
  // sibling-subdomain deployment that cookie is not the API's host-only CSRF
  // cookie and could overwrite the correct in-memory request header.
  withXSRFToken: false,
  xsrfCookieName: "XSRF-TOKEN",
  xsrfHeaderName: "X-XSRF-TOKEN",
  headers: { "Content-Type": "application/json" },
});

const EXPLICIT_URL_SCHEME = /^[a-z][a-z\d+.-]*:/i;
const SAFE_INLINE_ASSET = /^data:image\/(?:avif|gif|jpe?g|png|webp);base64,[a-z\d+/=\s]+$/i;

// Asset URLs arrive from several services and are ultimately used in image
// sources or document links. Keep ordinary HTTP(S), relative upload paths and
// tightly scoped inline raster payloads, but reject executable/custom URL
// schemes such as javascript:, data:text/html: and file:.
export const prefixUrl = (url) => {
  if (typeof url !== "string") return "";
  const candidate = url.trim();
  if (!candidate) return "";
  if (SAFE_INLINE_ASSET.test(candidate)) return candidate;
  if (candidate.toLowerCase().startsWith("data:") || (EXPLICIT_URL_SCHEME.test(candidate) && !/^https?:/i.test(candidate))) {
    return "";
  }

  const isHttpsProtocol = typeof window !== "undefined" && window.location.protocol === "https:";
  const configuredBase = BACKEND.endsWith("/api") ? BACKEND.substring(0, BACKEND.length - 4) : BACKEND;
  // Keep a configured HTTPS API origin for upload keys in split-domain hosting.
  // Never create mixed-content URLs when production/HTTPS was misconfigured
  // with an http:// backend; same-origin proxying is the only safe fallback.
  const base = (isHttpsProtocol || IS_PRODUCTION) && /^http:/i.test(configuredBase) ? "" : configuredBase;

  if (candidate.startsWith("/")) {
    return base ? `${base}${candidate}` : candidate;
  }
  if ((isHttpsProtocol || IS_PRODUCTION) && (candidate.includes("localhost:8080") || candidate.startsWith("http://"))) {
    return candidate.replace(/^http:\/\/[^\/]+/, base || (typeof window !== "undefined" ? window.location.origin : ""));
  }
  if (/^https?:/i.test(candidate)) return candidate;

  // A bare upload key such as `uploads/properties/photo.webp` is relative to
  // the API origin, not to whichever nested React route happens to be open.
  return `${base}/${candidate.replace(/^\.\//, "")}`;
};

export async function openPrivateDocument(rawUrl) {
  if (!rawUrl) return;
  const urlStr = typeof rawUrl === "string" ? rawUrl : (rawUrl.url || rawUrl.file_url || rawUrl.public_url || "");
  if (!urlStr) return;

  const fullUrl = prefixUrl(urlStr);
  if (urlStr.includes("/files/private/")) {
    try {
      let relativePath = urlStr.includes("http") ? new URL(urlStr).pathname : urlStr;
      relativePath = relativePath.replace(/^\/api(\/v1)?/, "");
      if (!relativePath.startsWith("/")) relativePath = "/" + relativePath;
      const response = await api.get(relativePath, { responseType: "blob" });
      const mimeType = urlStr.endsWith(".pdf") ? "application/pdf" : (response.headers["content-type"] || "application/octet-stream");
      const blob = new Blob([response.data], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
    } catch (err) {
      toast.error("Failed to open private document: " + formatApiError(err));
    }
  } else {
    window.open(fullUrl, "_blank");
  }
}

function normalizeProperty(p = {}) {
  if (!p || typeof p !== "object") return p;
  if (p.property && typeof p.property === "object" && !p.property.__normalized) {
    p.property = { ...normalizeProperty(p.property), __normalized: true };
  }
  const id = p.id || p.property_id;
  const rawThumbnail = p.thumbnail_url || p.public_url || p.thumbnail;

  let parsedRaw = p.raw;
  if (p.raw && typeof p.raw === "object" && typeof p.raw.value === "string") {
    try {
      parsedRaw = JSON.parse(p.raw.value);
    } catch (e) {
      console.warn("Failed to parse raw PGobject value", e);
    }
  } else if (typeof p.raw === "string") {
    try {
      parsedRaw = JSON.parse(p.raw);
    } catch (e) {
      console.warn("Failed to parse raw string", e);
    }
  }

  const thumbnail = prefixUrl(rawThumbnail);
  let images = Array.isArray(p.images)
    ? p.images.map(x => typeof x === "string" ? x : (x && (x.url || x.public_url))).filter(Boolean)
    : (thumbnail ? [thumbnail] : []);
  images = images.map(prefixUrl);
  const landDetails = p.land_details || p.land || {
    area: p.area,
    area_unit: p.area_unit,
    facing: p.facing,
    road_width_ft: p.road_width_ft,
    corner_plot: p.corner_plot,
    registry_available: p.registry_available,
    mutation_available: p.mutation_available,
    ownership_type: p.ownership_type,
  };
  const building = p.building_details || p.building || {};
  const normalizedRole = p.role ? String(p.role).toLowerCase().replace("office_user", "office") : p.role;
  const looksLikeLead = p.lead_id || p.source || p.buyer_user_id || p.buyer_name || p.buyer_phone || p.assigned_agent_id || p.assignment_status;
  const looksLikeVisit = p.site_visit_id || p.appointment_id || p.visit_at || p.scheduled_at || p.time_slot;
  const looksLikeDocument = p.document_id || p.document_type || p.verification_status;
  const looksLikeBooking = p.booking_id || p.booking_amount || p.booking_date;
  const looksLikeNotification = p.message || p.channel || p.template_key || p.read_at;
  const looksLikeOffice = p.office_id || p.office_name;
  const isNonProperty = looksLikeLead || looksLikeVisit || looksLikeDocument || looksLikeBooking || looksLikeNotification || looksLikeOffice || p.email || normalizedRole;

  return {
    ...p,
    raw: parsedRaw,
    thumbnail_url: thumbnail,
    thumbnail: prefixUrl(p.thumbnail),
    public_url: prefixUrl(p.public_url),
    avatar_url: prefixUrl(p.avatar_url),
    buyer_avatar_url: prefixUrl(p.buyer_avatar_url),
    assigned_to_avatar_url: prefixUrl(p.assigned_to_avatar_url),
    proposed_agent_avatar_url: prefixUrl(p.proposed_agent_avatar_url),
    role: normalizedRole,
    approved: p.status === "ACTIVE" || p.status === "approved" || p.approved,
    property_id: p.property_id || (looksLikeLead || looksLikeVisit || looksLikeDocument || looksLikeBooking || p.email || normalizedRole || looksLikeNotification ? p.property_id : id),
    user_id: p.user_id || ((p.email || normalizedRole) ? id : p.user_id),
    lead_id: p.lead_id || (looksLikeLead ? id : p.lead_id),
    site_visit_id: p.site_visit_id || p.appointment_id || (looksLikeVisit ? id : p.site_visit_id),
    appointment_id: p.appointment_id || p.site_visit_id || (looksLikeVisit ? id : p.appointment_id),
    document_id: p.document_id || (looksLikeDocument ? id : p.document_id),
    booking_id: p.booking_id || (looksLikeBooking ? id : p.booking_id),
    office_id: p.office_id || (normalizedRole === "office" ? id : p.office_id),
    notification_id: p.notification_id || (looksLikeNotification ? id : p.notification_id),
    _id: p._id || (looksLikeNotification ? id : p._id),
    read: p.read ?? (p.status === "read"),
    body: p.body || p.message,
    at: p.at || p.created_at,
    images: images.length ? images : (thumbnail ? [thumbnail] : []),
    area_sqft: p.area_sqft || p.area,
    land_details: landDetails,
    building_details: building,
    bedrooms: p.bedrooms ?? building.bedrooms ?? 0,
    bathrooms: p.bathrooms ?? building.bathrooms ?? 0,
    location: p.location || ((p.city || p.locality || p.address || p.latitude || p.longitude) ? {
      city: p.city,
      locality: p.locality,
      address: p.address,
      latitude: p.latitude,
      longitude: p.longitude
    } : null),
  };
}

function isBinaryData(data) {
  if (!data) return false;
  if (typeof Blob !== "undefined" && data instanceof Blob) return true;
  if (typeof ArrayBuffer !== "undefined" && data instanceof ArrayBuffer) return true;
  if (typeof FormData !== "undefined" && data instanceof FormData) return true;
  return false;
}

function normalizePayload(data) {
  if (!data || typeof data !== "object" || isBinaryData(data)) return data;
  if (Array.isArray(data)) return data.map(normalizeProperty);
  const out = { ...data };
  if (Array.isArray(out.items)) out.items = out.items.map(normalizeProperty);
  if (out.property && typeof out.property === "object") {
    out.property = normalizeProperty(out.property);
    if (Array.isArray(out.media)) {
      const mediaUrls = out.media.map(m => m.public_url || m.url || (m.path ? `/api/v1/files/${m.path}` : null)).filter(Boolean);
      if (mediaUrls.length) {
        out.property.images = mediaUrls;
      }
    }
  }
  if (out.id && !out.property_id && (out.title || out.category || out.price)) Object.assign(out, normalizeProperty(out));
  if (out.primary_role && !out.role) out.role = out.primary_role === "office_user" ? "office" : out.primary_role;
  if (out.role) out.role = String(out.role).toLowerCase().replace("office_user", "office");
  if (out.id && !out.user_id && (out.email || out.primary_role || out.role)) out.user_id = out.id;
  if (out.avatar_url) out.avatar_url = prefixUrl(out.avatar_url);
  if (out.selfie_url) out.selfie_url = prefixUrl(out.selfie_url);
  if (out.id_proof_url) out.id_proof_url = prefixUrl(out.id_proof_url);
  return out;
}

function normalizeMacroserviceEnvelope(response) {
  const body = response?.data;
  if (!body || typeof body !== "object" || isBinaryData(body)) return response;

  if (body.service && Object.prototype.hasOwnProperty.call(body, "data")) {
    response.rawData = body;
    response.data = normalizePayload(body.data);
    return response;
  }

  if (body.bff && Object.prototype.hasOwnProperty.call(body, "result")) {
    response.rawData = body;
    response.data = normalizePayload(body.result);
    return response;
  }

  response.data = normalizePayload(body);
  return response;
}

const NO_AUTO_REFRESH_ENDPOINT = /\/(?:v1\/)?auth\/(?:login|register|google\/verify|phone\/(?:register|verify)|refresh|logout(?:-all)?)(?:\?|$)/;
const CSRF_ENDPOINT = "/auth/csrf";
const UNSAFE_METHODS = new Set(["post", "put", "patch", "delete"]);
let csrfToken = null;
let csrfPromise = null;

function csrfFromResponse(response) {
  const body = response?.data;
  const envelopeData = body?.data;
  const token = body?.csrf_token || envelopeData?.csrf_token || response?.headers?.["x-xsrf-token"];
  if (typeof token === "string" && token) csrfToken = token;
  return response;
}

async function ensureCsrfToken() {
  if (csrfToken) return csrfToken;
  if (!csrfPromise) {
    csrfPromise = api.get(CSRF_ENDPOINT, { skipAuthRefresh: true, skipCsrf: true })
      .then((response) => {
        csrfFromResponse(response);
        if (!csrfToken) throw new Error("The server did not provide a CSRF token.");
        return csrfToken;
      })
      .finally(() => { csrfPromise = null; });
  }
  return csrfPromise;
}

export function clearCsrfToken() {
  csrfToken = null;
}

api.interceptors.request.use(async (config) => {
  // Explicitly tell the backend that this is a browser flow. It can then set
  // httpOnly cookies and omit bearer tokens from the JSON response while still
  // preserving token responses for non-browser/API clients.
  config.headers[COOKIE_AUTH_HEADER] = COOKIE_AUTH_MODE;

  const method = String(config.method || "get").toLowerCase();
  if (UNSAFE_METHODS.has(method) && !config.skipCsrf) {
    // Do not depend on JavaScript reading the XSRF cookie: in production the
    // frontend and API may be sibling subdomains. The server response is the
    // portable source of truth and this value lives only in module memory.
    config.headers[CSRF_HEADER] = await ensureCsrfToken();
  }
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }
  return config;
});

let refreshPromise = null;
let logoutPromise = null;
let logoutInProgress = false;
const LOGOUT_INTENT_TTL_MS = 60_000;

function hasActiveLogoutIntent() {
  if (typeof window === "undefined") return false;
  try {
    const announcedAt = Number(window.localStorage.getItem(AUTH_LOGOUT_INTENT_STORAGE_KEY));
    const age = Date.now() - announcedAt;
    const active = Number.isFinite(announcedAt) && announcedAt > 0
      && age >= 0 && age < LOGOUT_INTENT_TTL_MS;
    if (!active && announcedAt > 0) window.localStorage.removeItem(AUTH_LOGOUT_INTENT_STORAGE_KEY);
    return active;
  } catch {
    return false;
  }
}

function announceLogoutIntent() {
  if (typeof window === "undefined") return;
  try {
    // A short-lived intent closes most fallback races in browsers without Web
    // Locks: other tabs stop refreshing, and a refresh already in flight
    // revokes any newly rotated session as soon as its response arrives.
    window.localStorage.setItem(AUTH_LOGOUT_INTENT_STORAGE_KEY, String(Date.now()));
  } catch {
    // Storage-disabled browsers still retain the same-tab + Web Locks guards.
  }
}

export function clearLogoutIntent() {
  if (typeof window === "undefined") return;
  try { window.localStorage.removeItem(AUTH_LOGOUT_INTENT_STORAGE_KEY); } catch {}
}

function logoutIntentError() {
  const error = new Error("A browser logout is already in progress");
  error.code = "AUTH_LOGOUT_IN_PROGRESS";
  return error;
}

function canRefreshRequest(error) {
  const config = error?.config;
  if (logoutInProgress || error?.response?.status !== 401 || !config || config._cookieAuthRetried || config.skipAuthRefresh) {
    return false;
  }
  // Authentication-entry failures (bad password, rejected Google credential,
  // etc.) are final and must not be mistaken for an expired signed-in session.
  return !NO_AUTO_REFRESH_ENDPOINT.test(String(config.url || ""));
}

function isCsrfFailure(error) {
  const config = error?.config;
  if (error?.response?.status !== 403 || !config || config._csrfRetried || config.skipCsrf) {
    return false;
  }
  const data = error.response.data;
  const detail = typeof data === "string"
    ? data
    : [data?.error, data?.detail, data?.message].filter(Boolean).join(" ");
  return /csrf validation failed/i.test(detail);
}

function announceExpiredSession() {
  clearCsrfToken();
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new window.Event(AUTH_SESSION_EXPIRED_EVENT));
  }
}

export function notifyAuthSessionChange() {
  if (typeof window === "undefined") return;
  try {
    if (typeof window.BroadcastChannel === "function") {
      const channel = new window.BroadcastChannel(AUTH_CHANNEL_NAME);
      channel.postMessage({ type: "session-changed" });
      channel.close();
    }
  } catch {
    // Cross-tab synchronization is an enhancement; cookie auth remains valid.
  }
  try {
    // Storage events are the compatibility fallback for browsers without
    // BroadcastChannel. The transient value contains no credential or PII.
    window.localStorage.setItem(AUTH_SYNC_STORAGE_KEY, String(Date.now()));
    window.localStorage.removeItem(AUTH_SYNC_STORAGE_KEY);
  } catch {
    // Storage may be disabled; focus/visibility revalidation remains available.
  }
}

async function probeCurrentSession() {
  const response = await api.get("/auth/me", {
    skipAuthRefresh: true,
    headers: { "Cache-Control": "no-cache" },
  });
  const data = response?.data;
  const user = data?.user || data?.profile || data;
  if (!user || user.anonymous === true || (!user.id && !user.user_id)) {
    const error = new Error("Authentication session is missing or expired");
    error.response = { status: 401, data: { error: "Authentication session is missing or expired" } };
    throw error;
  }
  return response;
}

async function rotateOrReuseSession() {
  if (logoutInProgress || hasActiveLogoutIntent()) throw logoutIntentError();
  try {
    // A different tab may have rotated the shared cookie while this request was
    // waiting. Re-check before consuming the one-time refresh credential.
    await probeCurrentSession();
    return;
  } catch (probeError) {
    if (probeError?.response?.status !== 401) throw probeError;
  }

  await api.post("/auth/refresh", null, { skipAuthRefresh: true });
  if (logoutInProgress) {
    // The local logout path is waiting for this promise and will revoke the
    // rotated row immediately after it settles.
    throw logoutIntentError();
  }
  if (hasActiveLogoutIntent()) {
    // A different tab may have announced logout while this network request was
    // already in flight. Revoke the just-rotated server row instead of letting
    // its late Set-Cookie response silently restore the signed-in session.
    await api.post("/auth/logout", null, { skipAuthRefresh: true });
    throw logoutIntentError();
  }
  notifyAuthSessionChange();
}

async function refreshBrowserSession() {
  const locks = typeof window !== "undefined" ? window.navigator?.locks : null;
  if (locks?.request) {
    return locks.request(AUTH_LOCK_NAME, rotateOrReuseSession);
  }
  return rotateOrReuseSession();
}

/**
 * Revoke a browser session only after any local refresh has settled, and use
 * the same cross-tab Web Lock as refresh rotation. Otherwise a late refresh
 * response could install fresh cookies after logout had already cleared them.
 */
export function logoutBrowserSession() {
  if (logoutPromise) return logoutPromise;
  logoutInProgress = true;
  announceLogoutIntent();

  const performLogout = async () => {
    const pendingRefresh = refreshPromise;
    if (pendingRefresh) {
      try {
        await pendingRefresh;
      } catch {
        // Logout must still clear cookies after a failed refresh attempt.
      }
    }

    const requestLogout = () => api.post("/auth/logout", null, { skipAuthRefresh: true });
    const locks = typeof window !== "undefined" ? window.navigator?.locks : null;
    return locks?.request
      ? locks.request(AUTH_LOCK_NAME, requestLogout)
      : requestLogout();
  };

  logoutPromise = performLogout()
    .catch((error) => {
      // Logout did not happen, so other tabs may continue the valid session.
      clearLogoutIntent();
      throw error;
    })
    .finally(() => {
      logoutInProgress = false;
      logoutPromise = null;
    });
  return logoutPromise;
}

api.interceptors.response.use((response) => normalizeMacroserviceEnvelope(csrfFromResponse(response)), async (error) => {
  if (isCsrfFailure(error)) {
    const originalRequest = error.config;
    originalRequest._csrfRetried = true;
    clearCsrfToken();
    try {
      await ensureCsrfToken();
      return api(originalRequest);
    } catch {
      return Promise.reject(error);
    }
  }

  if (!canRefreshRequest(error)) return Promise.reject(error);

  const originalRequest = error.config;
  originalRequest._cookieAuthRetried = true;

  try {
    // Collapse simultaneous 401 responses into one refresh request. The new
    // access cookie is installed by the browser before queued calls are retried.
    if (!refreshPromise) {
      refreshPromise = refreshBrowserSession()
        .finally(() => { refreshPromise = null; });
    }
    await refreshPromise;
    return api(originalRequest);
  } catch (refreshError) {
    if (logoutInProgress || hasActiveLogoutIntent()) {
      return Promise.reject(refreshError);
    }
    // Browsers without Web Locks can race a rotating refresh cookie across
    // tabs. A losing tab must re-probe before treating the session as expired;
    // it never calls logout or clears the shared cookies.
    let finalProbeError = null;
    try {
      await new Promise((resolve) => setTimeout(resolve, 100));
      await probeCurrentSession();
      return api(originalRequest);
    } catch (probeError) {
      finalProbeError = probeError;
    }
    const definitivelyExpired = refreshError?.response?.status === 401 &&
      finalProbeError?.response?.status === 401;
    if (definitivelyExpired) {
      announceExpiredSession();
      return Promise.reject(error);
    }
    // Preserve in-memory auth during network/5xx/CSRF failures. Reject the
    // refresh/probe failure rather than the original 401 so callers do not
    // mistake a transient outage for definitive session expiry.
    const transientError = [refreshError, finalProbeError]
      .find((candidate) => candidate && candidate?.response?.status !== 401);
    return Promise.reject(transientError || refreshError || finalProbeError || error);
  }
});

export function asItems(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.properties)) return data.properties;
  if (Array.isArray(data.leads)) return data.leads;
  if (Array.isArray(data.users)) return data.users;
  if (Array.isArray(data.agents)) return data.agents;
  if (Array.isArray(data.offices)) return data.offices;
  if (Array.isArray(data.notifications)) return data.notifications;
  if (Array.isArray(data.bookings)) return data.bookings;
  if (Array.isArray(data.visits)) return data.visits;
  if (Array.isArray(data.site_visits)) return data.site_visits;
  if (Array.isArray(data.documents)) return data.documents;
  if (Array.isArray(data.reviews)) return data.reviews;
  if (Array.isArray(data.posts)) return data.posts;
  return [];
}

export function asObject(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {};
  return data;
}

export function formatApiError(err) {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const responseText = typeof data === "string" ? data : "";

  // Friendly messages for connectivity problems (no HTTP response received).
  if (err && !err.response) {
    if (err.code === "ECONNABORTED") return "The request timed out. Please try again.";
    if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
      const requestUrl = String(err?.config?.url || "");
      const uploadRequest = /\/(?:api\/)?upload(?:\?|$)/i.test(requestUrl);
      if (uploadRequest) {
        return "Upload failed before the server responded. Check image size/dimensions and server gateway limits (client_max_body_size / proxy timeout).";
      }
      return "Network error — please check your internet connection and try again.";
    }
  }

  // Some gateways return plain HTML on 413/502/504. Surface a clear message.
  if (responseText) {
    if (status === 413 || /request entity too large|payload too large|content length exceeded/i.test(responseText)) {
      return "The file is too large to upload.";
    }
    if (status === 504 || /gateway timeout|upstream timed out/i.test(responseText)) {
      return "Upload timed out at the gateway. Try a smaller image or retry.";
    }
    if (status === 502 || /bad gateway|upstream prematurely closed connection/i.test(responseText)) {
      return "Upload failed at the gateway. Please retry in a moment.";
    }
  }

  if (data?.validation_errors && Array.isArray(data.validation_errors)) return data.validation_errors.join(" ");
  const detail = data?.detail || data?.error || data?.message;
  if (!detail) {
    // Map bare HTTP statuses to clear, human messages.
    if (status === 401) return "Your session has expired. Please log in again.";
    if (status === 403) return "You don't have permission to perform this action.";
    if (status === 404) return "The requested item could not be found.";
    if (status === 413) return "The file is too large to upload.";
    if (status === 408 || status === 504) return "The upload request timed out. Please try a smaller image.";
    if (status === 502) return "Upload gateway error. Please retry shortly.";
    if (status >= 500) return "The server ran into a problem. Please try again shortly.";
    return err?.message || "Something went wrong. Please try again.";
  }
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}
export function extractUser(data = {}) {
  const u = data.user || data.profile || data;
  return normalizePayload(u);
}

export function formatPrice(n) {
  if (n == null || n === "") return "—";
  const amount = Number(n);
  if (!Number.isFinite(amount) || amount < 0) return "—";
  if (amount >= 1_00_00_000) return `₹${(amount / 1_00_00_000).toFixed(2)} Cr`;
  if (amount >= 1_00_000) return `₹${(amount / 1_00_000).toFixed(2)} L`;
  return `₹${amount.toLocaleString("en-IN")}`;
}

export function formatArea(n, unit = "sqft") {
  if (n == null || n === "") return "—";
  const area = Number(n);
  if (!Number.isFinite(area) || area <= 0) return "—";
  return `${area.toLocaleString("en-IN")} ${String(unit || "sqft").replaceAll("_", " ")}`;
}

export default api;


/**
 * SOLD lifecycle API helpers.
 * Purpose: keep sold-property calls in one place so admin, office, agent,
 * and future owner pages do not duplicate endpoint strings.
 */
export function markPropertySold(propertyId, reason = "Property sold request") {
  return api.post(`/properties/${propertyId}/mark-sold`, { reason });
}

/** Purpose: admin final approval for office/agent/owner sold verification requests. */
export function approveSoldVerification(propertyId, reason = "Admin verified property as sold") {
  return api.post(`/admin/properties/${propertyId}/sold/approve`, { reason });
}

/** Purpose: admin rejection restores property to previous active status in backend. */
export function rejectSoldVerification(propertyId, reason = "Admin rejected sold verification request", restoreStatus) {
  return api.post(`/admin/properties/${propertyId}/sold/reject`, {
    reason,
    restore_status: restoreStatus,
  });
}

/** Purpose: dashboards must use internal endpoints; public /properties shows only PUBLISHED. */
export function internalPropertiesEndpointForRole(role) {
  const r = String(role || "").toLowerCase().replace("office_user", "office");
  if (r === "admin") return "/admin/properties";
  if (r === "office") return "/office/properties";
  if (r === "agent") return "/agent/properties";
  return "/properties/me";
}
